import type * as lazyIndex from './lazyIndex';
import {
    createLazyComponent,
    LazyModule,
    registerLazyOrchestrator,
    LazyAction,
    type LazyComponent,
} from 'owa-bundling';
import onShowPostInstallSuccessDialog from 'owa-app-host-actions/lib/actions/onShowPostInstallSuccessDialog';
import { isDetailsDialogOpen } from 'owa-app-host-state/lib/store/dialogStore';
import { appStoreModalStore } from 'owa-app-host-state/lib/store/modalStore';
import type { ITaskModuleDialogProps } from './components/TaskModule/TaskModuleDialog';
import type { IStageViewModalProps } from './components/StageViewModal/StageViewModal';
import type { IJITInstallDialogProps } from './components/JITInstallDialog/JITInstallDialog';
import type { AppStoreModalProps } from './components/AppStoreModal/AppStoreModal';
import type { AppStoreDetailsDialogProps } from './components/AppStoreDetailsDialog/AppStoreDetailsDialog';
import type { ProjectionAppHostContainerProps } from './components/MetaOsAppHost/ProjectionAppHostContainer';
import type { AppHostContainerProps } from './components/MetaOsAppHost/AppHostContainer';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "AppHostComponents" */ './lazyIndex'),
    { name: 'AppHostComponents' }
);
const AppHostContainer: LazyComponent<AppHostContainerProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.AppHostContainer
);
const AppHostLoadingState: LazyComponent<unknown, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.AppHostLoadingState
);
const ProjectionAppHostContainer: LazyComponent<ProjectionAppHostContainerProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.ProjectionAppHostContainer);
const AppStoreDetailsDialog: LazyComponent<AppStoreDetailsDialogProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AppStoreDetailsDialog, undefined, undefined, {
        onlyImportIf: isDetailsDialogOpen,
    });
const AppStoreModal: LazyComponent<AppStoreModalProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.AppStoreModal,
    undefined,
    undefined,
    {
        onlyImportIf: () => appStoreModalStore.store().isOpen,
    }
);
const JITInstallDialog: LazyComponent<IJITInstallDialogProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.JITInstallDialog
);
const StageViewModal: LazyComponent<IStageViewModalProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.StageViewModal
);
const TaskModuleDialog: LazyComponent<ITaskModuleDialogProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.TaskModuleDialog
);
const DevicePermissionsDialogManager: LazyComponent<unknown, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.DevicePermissionsDialogManager
);

registerLazyOrchestrator(
    onShowPostInstallSuccessDialog,
    lazyModule,
    m => m.onShowPostInstallSuccessDialogOrchestrator
);

export {
    AppHostContainer,
    AppHostLoadingState,
    AppStoreDetailsDialog,
    AppStoreModal,
    DevicePermissionsDialogManager,
    JITInstallDialog,
    ProjectionAppHostContainer,
    StageViewModal,
    TaskModuleDialog,
};

export const lazyOpenTaskModule: LazyAction<typeof lazyIndex.openTaskModule, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.openTaskModule);
export const lazyUpdateTaskModule: LazyAction<
    typeof lazyIndex.updateTaskModule,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.updateTaskModule);
export const lazyOpenStageView: LazyAction<typeof lazyIndex.openStageView, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.openStageView);
